import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Data, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { HomeProject, HomeSimulation } from '@core/models/swagger';
import { Flow, RoutePaths } from '@global/routePaths';
import { select, Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { getProjectData, ProjectUpdateProject } from '@store/flow-data/project';
import {
  PutProjectRequest,
  selectPutProjectState,
  selectSimulationState,
  SimulationParams,
  SimulationRequest,
} from '@store/swagger/project';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import cloneDeep from 'clone-deep';
import { LoadingService } from '@core/services/loading/loading.service';

@Injectable({
  providedIn: 'root',
})
export class PackagesGuard implements CanActivate {
  homeProject: HomeProject;
  selectedSimulation: HomeSimulation;
  route: ActivatedRouteSnapshot;
  flow: Flow;
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly router: Router,
    private readonly store: Store<AppState>,
    private readonly loadingService: LoadingService
  ) {}

  cloneDeep<T>(value: T) {
    return cloneDeep(value);
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    this.route = route;
    this.flow = route.data.flow;
    this.store
      .pipe(select(getProjectData))
      .pipe(takeUntil(this.destroy$))
      .subscribe((project: HomeProject) => {
        this.homeProject = project;
      });

    this.postSimulation();
    return false;
  }

  postSimulation() {
    this.loadingService.presentLoading();
    const parameters: SimulationParams = {
      projectQuoteId: this.homeProject.projectQuoteId,
      version: this.homeProject.version,
    };
    this.store.dispatch(SimulationRequest({ parameters }));
    this.store
      .pipe(select(selectSimulationState))
      .pipe(
        takeUntil(this.destroy$),
        filter(({ loading }) => !loading),
        take(1)
      )
      .subscribe(({ data }) => {
        this.selectedSimulation = data['0'];
        this.putProject();
      });
  }

  putProject() {
    const homeProject = this.cloneDeep(this.homeProject);
    ((homeProject.homeRisks ??= [])[0] ??= {}).commercialProduct =
      this.selectedSimulation?.simulationCommercialProduct?.commercialProduct;
    ((homeProject.homeRisks ??= [])[0] ??= {}).coverages =
      this.selectedSimulation?.simulationCommercialProduct.coverages;
    homeProject.premiumDetail = { ...this.selectedSimulation?.premiumDetail };
    homeProject.premiumDiff = { ...this.selectedSimulation?.premiumDiff };
    homeProject.premiumDiscountAmn = { ...this.selectedSimulation?.premiumDiscountAmn };

    this.store.dispatch(ProjectUpdateProject({ data: homeProject }));
    this.store.dispatch(
      PutProjectRequest({
        body: homeProject,
        parameters: {
          projectQuoteId: homeProject.projectQuoteId,
        },
      })
    );
    this.store
      .pipe(select(selectPutProjectState))
      .pipe(
        filter(({ loading }) => !loading),
        take(1)
      )
      .subscribe(({ error }) => {
        if (error) {
          this.goNextStep();
        } else {
          this.goNextStep(this.route.data.parentPath);
        }
      });
  }

  goNextStep(parentPath?: Data[string]) {
    this.loadingService.dismissLoading();
    if (parentPath) {
      this.router.navigateByUrl(parentPath.COVERAGES.route, { state: { flow: this.flow, allowNavigation: true } });
    } else {
      this.router.navigateByUrl(RoutePaths.GENERIC_ERROR.route, {
        state: { continueToErrorPage: true, allowNavigation: true },
      });
    }
  }
}
